import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMessageLevel, EMessageType, MessagesUiService } from '@okcargo-apps/messages-ui';
import {
  EStopCorrectionTypeForShipper,
  IStopCorrectionForShipper,
  IValidationErrorForShipper,
  IValidationResultForShipper,
  UUID,
} from '@okcargo-apps/shared-domain';

@Injectable({
  providedIn: 'root',
})
export class FreightShipperValidationMessagesAdapterService {
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly translateService = inject(TranslateService);

  // constructor() {}

  public notifyMessages(result: IValidationResultForShipper, allStopIds: UUID[]): void {
    this.handleCorrections(result.corrections);
    this.cleanErrorMessages(allStopIds);
    this.handleErrors(result.errors);
  }

  // -- private

  private cleanErrorMessages(allStopIds: UUID[]): void {
    allStopIds.forEach((stopId, index) => {
      this.messagesUiService.removeMessageByCriteria({
        objectId: stopId,
        level: EMessageLevel.ERROR,
      });
    });
  }

  private handleCorrections(corrections: IStopCorrectionForShipper[]): void {
    corrections.forEach((correction) => {
      // only remove the same correctionType because the warning messages disappear automatically
      this.messagesUiService.removeMessageByCriteria({
        objectId: correction.stopId,
        type: this.mapCorrectionTypeToMessageType(correction.correctionType),
      });

      const messages: string[] = [
        this.translateService.instant(`error.EStopCorrectionType.${correction.correctionType}`),
      ];

      this.messagesUiService.setMessage({
        objectId: correction.stopId,
        message: messages,
        level: EMessageLevel.WARNING,
        type: this.mapCorrectionTypeToMessageType(correction.correctionType),
      });
    });
  }

  private handleErrors(errors: IValidationErrorForShipper[]): void {
    // Just set because all error messages have been cleaned at the beginnin

    const errorsByStopId = this.groupErrorsByStopId(errors);

    Object.entries(errorsByStopId).forEach(([stopId, stopErrors]) => {
      const errorMessages: string[] = stopErrors.map((error) =>
        this.translateService.instant(`error.EValidationErrorType.${error.errorType}`),
      );

      if (errorMessages.length > 1) {
        errorMessages.unshift('Por favor, corrija los siguientes errores:');
      }

      this.messagesUiService.setMessage({
        objectId: stopId as UUID,
        message: errorMessages,
        level: EMessageLevel.ERROR,
        type: EMessageType.STOP_ERRORS_GROUP,
      });
    });
  }

  private groupErrorsByStopId(errors: IValidationErrorForShipper[]): Record<UUID, IValidationErrorForShipper[]> {
    return errors.reduce(
      (grouped, error) => {
        if (!grouped[error.stopId]) {
          grouped[error.stopId] = [];
        }
        grouped[error.stopId].push(error);
        return grouped;
      },
      {} as Record<UUID, IValidationErrorForShipper[]>,
    );
  }

  private mapCorrectionTypeToMessageType(type: EStopCorrectionTypeForShipper): EMessageType {
    switch (type) {
      case EStopCorrectionTypeForShipper.STOP_UPDATED_TYPE:
        return EMessageType.STOP_UPDATED_TYPE;
      case EStopCorrectionTypeForShipper.STOP_UPDATED_UNLOADED_WEIGHT:
        return EMessageType.STOP_UPDATED_UNLOADED_WEIGHT;
      case EStopCorrectionTypeForShipper.STOP_UPDATED_UNLOADED_PACKAGES:
        return EMessageType.STOP_UPDATED_UNLOADED_PACKAGES;
      // default:
      //   return EMessageType.UNKNOWN;
    }
  }
}
